export default function CocoaBeachTravelTimingSection() {
  const cards = [
    {
      icon: "ri-ship-line",
      title: "Cruise Departures from Port Canaveral",
      content: (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Cocoa Beach sits just minutes south of Port Canaveral — the busiest cruise port in the world. Disney Cruise Line, Royal Caribbean, Carnival, Norwegian, and MSC all operate extensive Caribbean and Bahamas itineraries from Port Canaveral year-round. For Cocoa Beach residents, the port is practically in their backyard — cruises are not distant vacations, they are impulse weekend getaways booked with minimal lead time.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Cruise travel creates a unique passport urgency problem for Cocoa Beach locals. Because the cruise terminal is so close, residents often book last-minute sailings — sometimes just days before departure. Many discover expired or near-expired passports only when receiving their boarding documents, creating a crisis that standard processing cannot resolve. Cruise lines will deny boarding without exception, and travel insurance typically does not cover passport-related cancellations.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#F6C344]">Passport tip:</strong> Verify every traveler's passport expiration the moment you book a cruise — even a last-minute weekend sailing. Most cruise lines require at least six months of remaining validity. A passport expiring two months after your return date may still be rejected at embarkation.
          </p>
        </>
      ),
    },
    {
      icon: "ri-anchor-line",
      title: "Surf Travel & Swell Chasing",
      content: (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            As the East Coast surfing capital and hometown of Kelly Slater, Cocoa Beach has a surf culture that extends far beyond its own shoreline. Local surfers travel internationally to Costa Rica (Tamarindo, Jaco, Santa Teresa), Nicaragua (San Juan del Sur, Popoyo), Puerto Rico (Rincon, Aguadilla), and Indonesia (Bali, Mentawai) for world-class waves during swell seasons. The Atlantic hurricane season (June–November) generates the swells that make Central America a premier destination.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Surf travel is defined by its unpredictability — trips are booked on short notice when a storm forms and the swell forecast solidifies, often just one to two weeks before departure. The best waves don't wait for passport processing. A Cocoa Beach surfer who discovers an expired passport when a solid swell is approaching Costa Rica has limited options — standard processing is not one of them.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#F6C344]">Passport tip:</strong> Surfers: treat your passport like your board — keep it ready and in good condition. If you chase swells internationally, consider a second passport. Set a calendar reminder six months before expiration — hurricane season swells won't wait.
          </p>
        </>
      ),
    },
    {
      icon: "ri-rocket-line",
      title: "Space Industry & Launch Tourism",
      content: (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Cocoa Beach's proximity to Cape Canaveral Space Force Station and Kennedy Space Center creates a unique travel pattern distinct from other beach towns. Launch photographers, space journalists, industry contractors, and international space tourists travel to French Guiana (Ariane launches), Kazakhstan (Soyuz launches), and New Zealand (Rocket Lab launches) for global launch coverage. SpaceX and NASA employees travel internationally for partner missions, satellite integration, and ISS coordination meetings.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Launch campaigns are fluid — technical issues, weather delays, and range conflicts shift schedules constantly. A launch photographer credentialed for a Falcon Heavy mission may need to pivot to covering an Ariane 6 launch in French Guiana with two weeks' notice. An aerospace engineer supporting ISS payload integration may be called to a meeting in Toulouse or Tokyo on compressed timelines. These professionals need passport processing that matches their mission cadence.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#F6C344]">Passport tip:</strong> Space industry professionals: keep your passport current and check the six-month validity rule before every international launch or mission. Launch windows shift constantly — your passport needs to be ready when the countdown clock starts.
          </p>
        </>
      ),
    },
    {
      icon: "ri-sun-line",
      title: "Beachside Vacation & Festival Travel",
      content: (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Cocoa Beach's beachside residents and seasonal visitors travel internationally for spring break, summer vacation, and winter warm-weather getaways. Mexico (Cancun, Riviera Maya, Cabo), the Dominican Republic (Punta Cana), and the Bahamas are the most popular destinations. The Cocoa Beach Pier, Ron Jon Surf Shop, and the annual Easter Surf Festival draw international visitors who often extend their stays with Caribbean travel.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Snowbirds — seasonal residents who winter in Cocoa Beach from northern states and Canada — often book international trips from Florida rather than their home states. These travelers may discover expired passports mid-season, far from their home documents. The convenience of starting a passport application online from a Cocoa Beach condo rather than waiting to return north is a significant advantage for this demographic.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#F6C344]">Passport tip:</strong> Snowbirds: keep your passport and supporting documents with you when you travel to Florida for the season. Don't leave your birth certificate at your summer home — you may need it for a mid-season passport renewal.
          </p>
        </>
      ),
    },
  ];

  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Patterns</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Cocoa Beach Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Cocoa Beach's travel patterns blend last-minute cruise departures from Port Canaveral, surf-season swell chasing, space industry launch travel, and beachside vacation getaways. Understanding these patterns helps avoid the passport emergencies that disrupt your plans.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((card, i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                  <i className={`${card.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm leading-snug">{card.title}</h3>
              </div>
              <div>{card.content}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-[#1A365D] rounded-lg p-6 flex flex-col sm:flex-row items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
            <i className="ri-information-line text-[#1A365D] text-xl"></i>
          </div>
          <div>
            <p className="text-white font-bold text-sm mb-1">Cruise Season &amp; Launch Cadence Overlap</p>
            <p className="text-white/70 text-sm leading-relaxed">
              Cocoa Beach sees peak cruise departures from Port Canaveral during spring break (March–April), summer vacation (June–August), and the winter holiday season (December). Simultaneously, SpaceX and NASA launch campaigns from Cape Canaveral drive international travel on compressed timelines. These overlapping demand periods create a surge in passport urgency. If you are a Cocoa Beach resident with a cruise booked or a launch to cover, verify your passport status now — not the week before departure.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}